import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// iOS Safari never fires beforeinstallprompt and has no programmatic install —
// the only path is Share → Add to Home Screen, so we show instructions instead
// of a dead INSTALL button. (iPadOS 13+ reports as Macintosh but has touch.)
const isIOS =
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Don't show if already installed or previously dismissed
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if ((navigator as unknown as { standalone?: boolean }).standalone) return; // iOS home-screen PWA
    if (localStorage.getItem('pwa-prompt-dismissed')) return;

    // Only surface the prompt after the user has stamped at least once — the
    // banner sits over the mug on mobile and was eating taps on first-run.
    const maybeShow = () => {
      if (localStorage.getItem('bitbrew_first_stamp_done')) {
        setShowPrompt(true);
      }
    };

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      maybeShow();
    };

    const onFirstStamp = () => maybeShow();

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('bitbrew:first-stamp', onFirstStamp);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('bitbrew:first-stamp', onFirstStamp);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md">
      <div className="bg-white rounded-[16px] p-4 shadow-pixel">
        <div className="flex items-start gap-3">
          <span className="text-2xl">☕</span>
          <div className="flex-1">
            <p className="font-grotesk font-bold text-[13px] text-primary">
              ADD BIT BREW TO HOME SCREEN
            </p>
            <p className="mt-1 font-grotesk font-medium text-[12px] text-body-grey">
              Quick access to your loyalty card — just like a real app!
            </p>
            {isIOS && (
              <p className="mt-2 font-grotesk font-medium text-[12px] text-coral">
                Tap the SHARE button <span aria-hidden>⎋</span> in Safari, then
                &ldquo;ADD TO HOME SCREEN&rdquo;.
              </p>
            )}
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="flex-1 rounded-full bg-coral text-white font-grotesk font-bold text-[12px] py-2 px-3"
            >
              INSTALL
            </button>
          )}
          <button
            onClick={handleDismiss}
            className={`${isIOS ? 'flex-1 ' : ''}rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[12px] py-2 px-3 hover:border-coral hover:text-coral`}
          >
            {isIOS ? 'GOT IT' : 'LATER'}
          </button>
        </div>
      </div>
    </div>
  );
}
